import {computed, observable, makeObservable} from 'mobx';

import {BaseViewModel} from "../../../../infrastructure/models/base-view-model";
import {PredictionDomainModel} from "./prediction.domain-model";
import {InsightDomainModel} from "./insight/insight.domain-model";
import {PredictionOptionDomainModel} from "./option/prediction-option.domain-model";
import {OddsViewModel} from "../../../common/odds/models/odds.view-model";
import {ODDS_TYPES} from "../../../../core/components/odds/components/odds-consts";
import {EXTRA_LINKS_CONTEXTS, GAME_STATUSES} from "../../../../core/app/consts";
import {PredictionsOptionViewModel} from "./predictions.option.view-model";

export class PredictionViewModel extends BaseViewModel {
  domainModel: PredictionDomainModel;
  @observable oddsType: ODDS_TYPES;
  @observable _selectedVoteNum: Number;
  context: EXTRA_LINKS_CONTEXTS;
  liveMode: Boolean;
  index: Number;

  constructor(index: Number, domainModel: PredictionDomainModel, selectedVoteNum: Number = -1, oddsType: ODDS_TYPES, gameStatus: GAME_STATUSES, gameId: Number, context: EXTRA_LINKS_CONTEXTS, liveMode: Boolean = false) {
    super(domainModel);

    this.index = index;
    this.oddsType = oddsType;
    this.gameId = gameId;
    this.gameStatus = gameStatus;
    this._selectedVoteNum = selectedVoteNum;
    this.context = context;
    this.liveMode = liveMode;

    makeObservable(this);
  }

  get id(): Number {
    return this.domainModel.id;
  }

  get type(): Number {
    return this.domainModel.type;
  }

  get title(): String {
    return this.domainModel.title;
  }

  get hasRecords(): String {
    return this.domainModel.hasRecords;
  }

  set selectedVoteNum(num) {
    this._selectedVoteNum = num;
  }

  @computed get selectedVoteNum(): Boolean {
    if (this._selectedVoteNum > 0) {
      return this._selectedVoteNum;
    }

    if (this.gameStatus !== GAME_STATUSES.scheduled && !this.liveMode) {
      return this.highestVotedNum;
    }

    return -1;
  }

  @computed get enableVoting(): Boolean {
    return this.selectedVoteNum === -1;
  }

  get highestVotedNum(): Number {
    let highestVotes: Number = 0;
    let highestVotedNum: Number = -1;

    this.domainModel.options.forEach((option: PredictionsOptionViewModel) => {
      if (option.vote.count > highestVotes) {
        highestVotes = option.vote.count;
        highestVotedNum = option.num;
      }
    });

    return highestVotedNum;
  }

  get hasInsight(): Boolean {
    return !!this.domainModel.insight;
  }

  get insightText(): InsightDomainModel {
    return this.hasInsight && this.domainModel.insight.text;
  }

  get statisticalAnalysis(): InsightDomainModel {
    return this.hasInsight && this.domainModel.insight.statisticalAnalysis;
  }

  get showVotes(): Boolean {
    return this.domainModel.showVotes;
  }

  get totalVotes(): Boolean {
    return this.domainModel.totalVotes;
  }

  @computed get hasOdds(): Boolean {
    return !!this.domainModel.odds;
  }

  get shouldRenderBookmakerActionButton(): Boolean {
    const hasBookmakerActionButton: Boolean = this.hasOdds && !!this.odds.bookmaker && !!this.odds.bookmaker.actionButton;
    const isGameFinished: Boolean = this.gameStatus === GAME_STATUSES.ended;

    return hasBookmakerActionButton && !isGameFinished;
  }

  @computed get odds(): OddsViewModel {
    if (!this.hasOdds) {
      return null;
    }

    return new OddsViewModel(this.domainModel.odds, this.oddsType, this.context);
  }

  @computed get options(): Array<PredictionsOptionViewModel> {
    let delay: Number = 0;
    const options: Array<PredictionsOptionViewModel> = this.domainModel.options.map((option: PredictionOptionDomainModel) => {
      const prediction = new PredictionsOptionViewModel(option, this.selectedVoteNum, delay);
      delay += prediction.animationDuration;
      return prediction;
    });

    return options;
  }

  get hasTwoOption(): Boolean {
    const hasTwoOptions: Boolean = this.options.length === 2;

    return hasTwoOptions;
  }
}
